import { cn } from 'src/lib/utils';
import type { SelfServiceCampaignCreative, SelfServiceCampaignVariant } from '../types/self-service-campaign.types';
import { formatLaunchedAt } from '../utils/campaigns.utils';
import { getPrimaryVariants, isVideoMimeType } from '../utils/campaign-creative.utils';
import { formatSelfServiceStatus } from '../utils/self-service-campaign.utils';

function VariantMedia({
  variant,
  className,
}: {
  variant: SelfServiceCampaignVariant;
  className?: string;
}) {
  const mediaUrl = variant.media_url;

  if (!mediaUrl) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-md border border-dashed border-[#D9DBE3] bg-white text-[11px] text-[#9CA3AF]',
          className,
        )}
      >
        No media
      </div>
    );
  }

  if (isVideoMimeType(variant.mime_type)) {
    return (
      <video
        src={mediaUrl}
        controls
        className={cn('rounded-md border border-[#EDEEF2] bg-black object-contain', className)}
      >
        <track kind="captions" />
      </video>
    );
  }

  return (
    <img
      src={mediaUrl}
      alt={variant.name}
      className={cn('rounded-md border border-[#EDEEF2] bg-white object-contain', className)}
    />
  );
}

function PrimaryVariantCard({ variant }: { variant: SelfServiceCampaignVariant }) {
  const dimensions = variant.width && variant.height
    ? `${variant.width}×${variant.height}`
    : null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[#EDEEF2] bg-white p-3">
      <VariantMedia
        variant={variant}
        className="max-h-48 w-full"
      />
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-[#0D0F1A]">
          {variant.name}
        </p>
        <p className="mt-1 text-[12px] text-[#5E6278]">
          {[
            variant.size_key,
            dimensions,
            formatSelfServiceStatus(variant.status),
          ].filter(Boolean).join(' · ')}
        </p>
        {variant.latest_overall_score !== null ? (
          <p className="mt-1 text-[12px] text-[#5E6278]">
            Score
            {' '}
            {variant.latest_overall_score.toFixed(1)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function CampaignCreativeCard({ creative }: { creative: SelfServiceCampaignCreative }) {
  const primaryVariants = getPrimaryVariants(creative);

  return (
    <li className="rounded-lg border border-[#EDEEF2] bg-[#FAFAFB] p-4">
      <div className="mb-4">
        <p className="font-medium text-[#0D0F1A]">
          {creative.name}
          {creative.is_primary ? (
            <span className="ml-2 text-[12px] font-normal text-[#6028B4]">
              Primary creative
            </span>
          ) : null}
        </p>
        <p className="mt-1 text-[13px] text-[#5E6278]">
          {formatSelfServiceStatus(creative.type)}
          {creative.creator_name ? ` · ${creative.creator_name}` : ''}
          {creative.score !== null ? ` · Score ${creative.score.toFixed(1)}` : ''}
        </p>
        <p className="mt-1 font-mono text-[12px] text-[#9CA3AF]">
          {formatLaunchedAt(creative.created_at)}
        </p>
      </div>

      {primaryVariants.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {primaryVariants.map((variant) => (
            <PrimaryVariantCard
              key={variant.id}
              variant={variant}
            />
          ))}
        </div>
      ) : (
        <p className="text-[13px] text-[#9CA3AF]">
          No primary variants available.
        </p>
      )}
    </li>
  );
}
